import { observer } from 'mobx-react-lite';
import { Loader2, Power, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cameraStatusToClient } from '@/lib/clientStatus';
import type { StatusModal as StatusModalCtrl } from '@/controllers/clients/clientCard/useStatusModal';

type Props = { ctrl: StatusModalCtrl };

export const StatusModal = observer(function StatusModal({ ctrl }: Props) {
  const isActive = ctrl.currentStatus === 'Активный';
  const newStatus = isActive ? 'Неактивный' : 'Активный';

  return (
    <Modal
      isOpen={ctrl.isOpen}
      onClose={ctrl.close}
      title={isActive ? 'Отключить камеру' : 'Включить камеру'}
      subtitle={ctrl.cameraName}
      size='md'
      footer={
        <>
          <Button variant='outline' onClick={ctrl.close} className='flex-1'>
            Отмена
          </Button>
          <Button
            onClick={ctrl.submit}
            disabled={ctrl.isLoading}
            className={cn(
              'flex-1 gap-2',
              isActive
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            )}
          >
            {ctrl.isLoading ? (
              <Loader2 className='w-4 h-4 animate-spin' />
            ) : (
              <Power className='w-4 h-4' />
            )}
            {isActive ? 'Отключить' : 'Включить'}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div className='flex items-center gap-4 p-3.5 bg-muted/30 border border-border/30 rounded-xl'>
          <div className='text-center flex-1'>
            <p className='text-xs text-muted-foreground mb-1'>Текущий</p>
            <StatusBadge
              status={cameraStatusToClient(ctrl.currentStatus)}
              label={ctrl.currentStatus}
              size='sm'
            />
          </div>
          <div className='text-muted-foreground'>→</div>
          <div className='text-center flex-1'>
            <p className='text-xs text-muted-foreground mb-1'>Новый</p>
            <StatusBadge status={cameraStatusToClient(newStatus)} label={newStatus} size='sm' />
          </div>
        </div>
        <div>
          <Label className='text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5'>
            Комментарий
          </Label>
          <Input
            type='text'
            placeholder='Необязательно'
            value={ctrl.comments}
            onChange={(e) => ctrl.setComments(e.target.value)}
            className='bg-muted/30 border-border/50 focus-visible:border-primary/50 focus-visible:ring-0'
          />
        </div>
        {ctrl.error && (
          <div className='flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-xl text-destructive text-sm'>
            <AlertCircle className='w-4 h-4 shrink-0' />
            {ctrl.error}
          </div>
        )}
      </div>
    </Modal>
  );
});
